import { useEffect, useMemo, useState, useRef } from "react";
import { Loader2, Clock, ClipboardList, Clapperboard, ChevronRight, Users as UsersIcon, Film, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import { uploadProjectMedia, entryThumbSrc } from "../lib/media";
import { enqueuePendingUpload, isOfflineError } from "../lib/offline";
import { sortCharacters } from "../lib/characters";
import { sortScenes, formatSceneNumber } from "../lib/scenes";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import AssignmentScenePicker from "./AssignmentScenePicker";
import AssignmentCharacterPicker from "./AssignmentCharacterPicker";

/**
 * mode: "create" | "edit"
 * image: { full, thumb, fileName, source } — required for create
 * initialEntry: existing entry — required for edit
 * initialContext: { mode?: 'onset'|'prep', scene_id?, character_id? } — optional prefill for create
 */
export default function AssignmentDialog({
    projectId,
    open,
    onClose,
    mode = "create",
    image = null,
    images = null,  // iter-45: optional batch payload — array of {file, full, thumb, fileName}.
    initialEntry = null,
    initialContext = null,
    onSaved,
    onAssignLater,
}) {
    const apiBase = useApiBase();
    // Normalise the input. If a caller passes the legacy `image` prop we
    // treat it as a 1-item batch internally; everything below operates
    // on `imageBatch` so the create flow is the same shape regardless.
    const imageBatch = useMemo(() => {
        if (Array.isArray(images) && images.length > 0) return images;
        if (image) return [image];
        return [];
    }, [images, image]);
    const isBatch = imageBatch.length > 1;
    const [batchIndex, setBatchIndex] = useState(0); // for progress display only
    const [scenes, setScenes] = useState([]);
    const [characters, setCharacters] = useState([]);
    // iter-105 — episode awareness inside the assignment / capture
    // dialog. `episodes` is the authoritative project list (source of
    // truth = backend /episodes endpoint); `episodeFilter` is the
    // active selection for narrowing scenes + cast (null → "All").
    const [episodes, setEpisodes] = useState([]);
    const [episodeFilter, setEpisodeFilter] = useState(null);
    // Effective permissions for the current user on this project. Loaded
    // from /permissions/me alongside the scene + cast fetch so the UI
    // can gate inline creation correctly without a flicker.
    const [permissions, setPermissions] = useState({ can_create_characters: false });
    const [isOwner, setIsOwner] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [hasPendingRequest, setHasPendingRequest] = useState(false);
    const [requestingPerm, setRequestingPerm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [later, setLater] = useState(false);
    const [error, setError] = useState("");

    const [entryMode, setEntryMode] = useState("onset"); // 'onset' | 'prep'
    // Iter-118 — multi-scene assignment. One image can now be linked
    // to N scenes (Cartesian'd with `characterIds` so a single media
    // file backs every (scene, character) state_entry row). In EDIT
    // mode we hydrate from every sibling row that shares the entry's
    // media_id so the user sees the FULL current grouping before
    // they tweak it.
    const [sceneIds, setSceneIds] = useState([]);
    // iter-71: multi-character assignment. CREATE mode lets the user pick
    // multiple characters; one source image is then linked to each via
    // /state-entries/bulk-assign (the file is uploaded ONCE, then N
    // state_entries reference the same media_id). Iter-118: EDIT mode
    // also supports multi via the new /state-entries/sync endpoint.
    const [characterIds, setCharacterIds] = useState([]);
    // State label is no longer surfaced in the UI per iter-45 — every
    // new entry is saved as `state_label="custom"` with no `custom_label`,
    // which renders as a neutral chip in older views and is harmless for
    // continuity. Existing entries keep whatever label they had.
    const [stateLabel] = useState("custom");
    const [customLabel] = useState("");
    // iter-75 — free-form continuity Look ("Clean", "Bloody nose"…).
    // Optional, editable post-save, surfaced everywhere the entry
    // shows up. Kept separate from `note` so set crews can scan a
    // glance-card without parsing prose.
    const [look, setLook] = useState("");
    const [recentLooks, setRecentLooks] = useState([]);
    const [note, setNote] = useState("");

    const [creatingChar, setCreatingChar] = useState(false);
    const [newCharNumber, setNewCharNumber] = useState("");
    const [newCharName, setNewCharName] = useState("");
    const [newCharActor, setNewCharActor] = useState("");
    // Default to Crowd — most inline-create cases on set are Crowd
    // entries (e.g. "Background Diner #65"), per the user's spec.
    const [newCharTeam, setNewCharTeam] = useState("crowd");
    const [creatingCharSubmitting, setCreatingCharSubmitting] = useState(false);

    // Team filter chips above the cast list. Same affordance the user
    // already sees in the Cast hub, kept in component-local state so
    // each open of the dialog starts at the safe "All" default.
    const [teamFilter, setTeamFilter] = useState("all");

    // Lightweight inline search for scene + character pickers. As soon as
    // a project has more than a handful of scenes / cast, scrolling becomes
    // slow on phones — so a 1-line filter beats a tall list every time.
    const [sceneQuery, setSceneQuery] = useState("");
    const [charQuery, setCharQuery] = useState("");

    // Iter-119 — full-screen picker shells. The main dialog now
    // renders compact "trigger rows"; tapping one slides up a
    // dedicated picker overlay so the on-set user has only one
    // task on screen at a time.
    const [scenePickerOpen, setScenePickerOpen] = useState(false);
    const [charPickerOpen, setCharPickerOpen] = useState(false);

    // Iter-119 — Look combobox state. Focused state drives the
    // suggestion popover; characterLooks holds the per-character
    // recent values so we can surface "for this actor" recall
    // ahead of the global recents.
    const [lookFocused, setLookFocused] = useState(false);
    const [characterLooks, setCharacterLooks] = useState([]);
    const lookInputRef = useRef(null);
    const lookWrapRef = useRef(null);

    const canCreateCharacters = isOwner || isAdmin || permissions?.can_create_characters;

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setError("");
        Promise.all([
            api.get(`${apiBase}/${projectId}/scenes`),
            api.get(`${apiBase}/${projectId}/characters`),
            api.get(`${apiBase}/${projectId}`),
            api.get(`${apiBase}/${projectId}/permissions/me`),
            // iter-75 PART 3 — recent Look autocomplete. Best-effort:
            // a 4xx/5xx here MUST NOT block the dialog open, since
            // looks are optional. Wrap to a no-op array on failure.
            api.get(`${apiBase}/${projectId}/looks`).catch(() => ({ data: { looks: [] } })),
            // iter-105 — distinct episode list, best-effort. Empty on
            // film projects so the EpisodeFilter component hides.
            api
                .get(`${apiBase}/${projectId}/episodes`)
                .catch(() => ({ data: { episodes: [] } })),
        ])
            .then(([{ data: sc }, { data: ch }, { data: p }, { data: perms }, { data: looks }, { data: eps }]) => {
                setScenes(sortScenes(sc || []));
                setCharacters(sortCharacters(ch || []));
                setIsOwner(p?.role === "owner");
                setIsAdmin(p?.role === "admin");
                setPermissions(perms?.permissions || { can_create_characters: false });
                setHasPendingRequest(Boolean(perms?.has_pending_request));
                setRecentLooks(Array.isArray(looks?.looks) ? looks.looks : []);
                setEpisodes(eps?.episodes || []);
                // Iter-114c — guarantee the originating scene is visible
                // in the picker even if it isn't in the confirmed list
                // (e.g. when capture is launched from a continuity block
                // whose scene status is still draft). Fetches that single
                // scene by id and merges so the chip can be pre-selected.
                const seedSceneId =
                    initialEntry?.scene_id || initialContext?.scene_id;
                if (
                    seedSceneId &&
                    !(sc || []).some((s) => s.id === seedSceneId)
                ) {
                    api
                        .get(`${apiBase}/${projectId}/scenes/${seedSceneId}`)
                        .then(({ data: extra }) => {
                            if (extra && extra.id) {
                                setScenes((cur) =>
                                    cur.some((s) => s.id === extra.id)
                                        ? cur
                                        : [...cur, extra]
                                );
                            }
                        })
                        .catch(() => {
                            /* silent — picker just won't surface it */
                        });
                }
                // Iter-118 — in EDIT mode hydrate from all sibling
                // state_entries that share this image's media_id so
                // the full multi-scene × multi-character grouping is
                // visible before the user tweaks it. Falls back to
                // the single (scene_id, character_id) pair when the
                // entry has no media_id (legacy inline images).
                if (mode === "edit" && initialEntry) {
                    if (initialEntry.media_id) {
                        api
                            .get(`${apiBase}/${projectId}/state-entries`, {
                                params: { media_id: initialEntry.media_id },
                            })
                            .then(({ data: siblings }) => {
                                const sIds = Array.from(
                                    new Set(
                                        (siblings || [])
                                            .map((e) => e.scene_id)
                                            .filter(Boolean),
                                    ),
                                );
                                const cIds = Array.from(
                                    new Set(
                                        (siblings || [])
                                            .map((e) => e.character_id)
                                            .filter(Boolean),
                                    ),
                                );
                                if (sIds.length > 0) setSceneIds(sIds);
                                if (cIds.length > 0) setCharacterIds(cIds);
                                // Backfill any scene rows the picker
                                // doesn't yet know about (status !=
                                // confirmed). Same pattern as the
                                // single-scene seed above.
                                const known = new Set(
                                    (sc || []).map((s) => s.id),
                                );
                                const missing = sIds.filter(
                                    (id) => !known.has(id),
                                );
                                if (missing.length > 0) {
                                    Promise.all(
                                        missing.map((id) =>
                                            api
                                                .get(
                                                    `${apiBase}/${projectId}/scenes/${id}`,
                                                )
                                                .then((r) => r.data)
                                                .catch(() => null),
                                        ),
                                    ).then((extras) => {
                                        const fresh = extras.filter(Boolean);
                                        if (fresh.length === 0) return;
                                        setScenes((cur) => {
                                            const seen = new Set(
                                                cur.map((s) => s.id),
                                            );
                                            return [
                                                ...cur,
                                                ...fresh.filter(
                                                    (s) => !seen.has(s.id),
                                                ),
                                            ];
                                        });
                                    });
                                }
                            })
                            .catch(() => {
                                /* fallback handled by the lone-entry effect below */
                            });
                    }
                }
            })
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [open, projectId]);

    useEffect(() => {
        if (!open) return;
        if (mode === "edit" && initialEntry) {
            setEntryMode(initialEntry.mode || "onset");
            // Iter-118 — seed from the single entry FIRST so the
            // dialog has something useful even before the media-
            // siblings fetch resolves. The async fetch above
            // overrides this with the full grouping when it lands.
            setSceneIds(initialEntry.scene_id ? [initialEntry.scene_id] : []);
            setCharacterIds(initialEntry.character_id ? [initialEntry.character_id] : []);
            setLook(initialEntry.look || "");
            setNote(initialEntry.note || "");
            // iter-105 — for edits, default the episode filter to the
            // existing entry's scene episode so the user sees that
            // episode's scenes/cast first. Resolved once scenes load
            // (see effect below).
        } else {
            // iter-73 PART 1 — On Set is ALWAYS the default in
            // create mode. The user explicitly asked for this even
            // when capturing from the Fittings list page: continuity
            // images on shoot day overwhelmingly belong to On Set,
            // and Fittings stays one tap away. We still honour an
            // explicit `mode: "prep"` in initialContext so deep
            // links (e.g. "Capture for THIS fitting") work, but the
            // bare-context capture-FAB invocation no longer leaks
            // the page's own mode into the dialog.
            setEntryMode(initialContext?.mode === "prep" ? "prep" : "onset");
            // Iter-118 — accept either a singular scene_id (legacy
            // callers) or a scene_ids array.
            const seedScenes = Array.isArray(initialContext?.scene_ids)
                ? initialContext.scene_ids
                : initialContext?.scene_id
                    ? [initialContext.scene_id]
                    : [];
            setSceneIds(seedScenes);
            // initialContext can pre-seed either a single character_id
            // (legacy callers) or a character_ids array (iter-71 callers).
            const seedIds = Array.isArray(initialContext?.character_ids)
                ? initialContext.character_ids
                : initialContext?.character_id
                    ? [initialContext.character_id]
                    : [];
            setCharacterIds(seedIds);
            // iter-75 — preserve the just-used Look across rapid
            // multi-shot capture sessions on the same set-up.
            setLook(initialContext?.look || "");
            setNote("");
        }
        // iter-105 — context-aware default for the episode filter.
        // The most reliable source is `initialContext.episode` (when
        // a caller has it). Otherwise we resolve once scenes land
        // (effect below) by looking up the seed scene's episode.
        if (initialContext?.episode) {
            setEpisodeFilter(String(initialContext.episode));
        } else {
            setEpisodeFilter(null);
        }
        // Reset filters every time the dialog opens — picking up where the
        // last user left off in the dialog is rarely what you want.
        setSceneQuery("");
        setCharQuery("");
        setTeamFilter("all");
        setCreatingChar(false);
        setNewCharNumber("");
        setNewCharName("");
        setNewCharActor("");
        setNewCharTeam("crowd");
    }, [open, mode, initialEntry, initialContext]);

    // iter-105 — once scenes load, if we were given a seed scene
    // (either via `initialContext.scene_id` for create or via the
    // existing entry for edit), narrow the episode filter to that
    // scene's episode so the picker opens already focused on the
    // relevant episode.
    useEffect(() => {
        if (!open) return;
        if (episodeFilter) return;  // user/context already set one
        const seedSceneId = mode === "edit"
            ? initialEntry?.scene_id
            : (sceneIds[0] || initialContext?.scene_id);
        if (!seedSceneId || !scenes?.length) return;
        const seedScene = scenes.find((s) => s.id === seedSceneId);
        if (seedScene?.episode) {
            setEpisodeFilter(String(seedScene.episode));
        }
        // we intentionally only depend on `scenes` + open here — once
        // we've nudged the filter to match the seed, future changes
        // are owned by the user.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, scenes]);

    // Iter-119 — character-aware Look suggestions. When at least
    // one character is picked we fetch the recent looks scoped to
    // the FIRST one (the "primary" character — most assignments
    // are single-character) so the suggestions reflect what that
    // actor actually wears, not project-wide chatter. Falls back
    // silently to the project-recent list if the request fails or
    // returns nothing.
    useEffect(() => {
        if (!open) {
            setCharacterLooks([]);
            return;
        }
        const primary = characterIds[0];
        if (!primary) {
            setCharacterLooks([]);
            return;
        }
        let cancelled = false;
        api
            .get(`${apiBase}/${projectId}/looks`, {
                params: { character_id: primary },
            })
            .then(({ data }) => {
                if (cancelled) return;
                setCharacterLooks(
                    Array.isArray(data?.looks) ? data.looks : [],
                );
            })
            .catch(() => {
                /* silent — fall back to global recents */
            });
        return () => {
            cancelled = true;
        };
    }, [open, projectId, characterIds]);

    // Iter-119 — close the Look popover on outside click. Tap-away
    // dismissal keeps the Save button clear of obstructions on
    // small screens.
    useEffect(() => {
        if (!lookFocused) return;
        const handler = (e) => {
            if (!lookWrapRef.current) return;
            if (!lookWrapRef.current.contains(e.target)) {
                setLookFocused(false);
            }
        };
        window.addEventListener("mousedown", handler);
        window.addEventListener("touchstart", handler);
        return () => {
            window.removeEventListener("mousedown", handler);
            window.removeEventListener("touchstart", handler);
        };
    }, [lookFocused]);

    const close = (force = false) => {
        if (saving && !force) return;
        setCreatingChar(false);
        setNewCharNumber("");
        setNewCharName("");
        setNewCharActor("");
        setNewCharTeam("crowd");
        setScenePickerOpen(false);
        setCharPickerOpen(false);
        setLookFocused(false);
        setError("");
        onClose?.();
    };

    const createCharacterInline = async (e) => {
        e.preventDefault();
        const name = newCharName.trim();
        if (!name) return;
        setCreatingCharSubmitting(true);
        try {
            const { data } = await api.post(`${apiBase}/${projectId}/characters`, {
                name,
                actor: newCharActor.trim() || null,
                character_number: newCharNumber.trim() || null,
                team: newCharTeam,
            });
            setCharacters((prev) => [...prev, data]);
            // Auto-select the new character so the assign flow continues
            // without an extra tap. Per spec: "After creation, automatically
            // select the new character". iter-71: multi-select — append
            // to the existing selection instead of replacing it so the
            // user can keep stacking characters onto the same image.
            setCharacterIds((prev) => (prev.includes(data.id) ? prev : [...prev, data.id]));
            // Pull the team filter to match the freshly created character
            // so it's immediately visible in the list (helpful when the
            // user was filtering "Main" but just added a Crowd entry).
            if (teamFilter !== "all" && (data.team || "main") !== teamFilter) {
                setTeamFilter(data.team || "main");
            }
            setCreatingChar(false);
            setNewCharNumber("");
            setNewCharName("");
            setNewCharActor("");
            setNewCharTeam("crowd");
            toast.success(`Added ${data.name}`);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setCreatingCharSubmitting(false);
        }
    };

    const requestCreatePermission = async () => {
        if (hasPendingRequest || requestingPerm) return;
        setRequestingPerm(true);
        try {
            await api.post(`${apiBase}/${projectId}/permission-requests`, {
                kind: "create_characters",
            });
            setHasPendingRequest(true);
            toast.success("Request sent to the project owner");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRequestingPerm(false);
        }
    };

    // iter-105 — quick lookup of which episodes each character
    // appears in. The list endpoint returns `episodes: string[]` per
    // character (iter-101); we just need it indexed by id for fast
    // checks during render.
    const charEpisodesById = useMemo(() => {
        const m = new Map();
        for (const c of characters) m.set(c.id, c.episodes || []);
        return m;
    }, [characters]);

    const filteredScenes = useMemo(() => {
        const q = sceneQuery.trim().toLowerCase();
        let list = scenes;
        // iter-105 — episode narrows the picker to a single episode
        // FIRST, before text search, so "Sc 3" inside Ep 4 doesn't
        // surface Ep 1's Sc 3 by accident.
        if (episodeFilter) {
            list = list.filter(
                (s) => String(s.episode || "") === String(episodeFilter),
            );
        }
        if (!q) return list;
        return list.filter((s) =>
            [s.scene_number, s.heading, s.int_ext, s.day_night, s.episode]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(q),
        );
    }, [scenes, sceneQuery, episodeFilter]);

    const filteredCharacters = useMemo(() => {
        const q = charQuery.trim().toLowerCase();
        let list = characters;
        // iter-105 — when an episode filter is active, prioritise
        // characters who appear in that episode. Manually-created
        // characters (no scene attachments → empty `episodes` array)
        // are kept visible because crowd / extra rows often need to
        // be assigned before they're attached to any scene.
        if (episodeFilter) {
            list = list.filter((c) => {
                const eps = c.episodes || [];
                if (eps.length === 0) return true; // unattached → still pickable
                return eps.includes(String(episodeFilter));
            });
        }
        // Team chip filter (All / Main / Crowd) lives above the search.
        // A null / undefined team falls into "main" by convention to
        // match the iter-27 default.
        if (teamFilter !== "all") {
            list = list.filter((c) => (c.team || "main") === teamFilter);
        }
        if (!q) return list;
        return list.filter((c) =>
            // `character_number` is now part of the haystack so users can
            // type "65" and find "#65 Bob" — the same affordance the On
            // Set search added.
            [c.name, c.actor, c.character_number]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(q),
        );
    }, [characters, charQuery, teamFilter, episodeFilter]);

    // iter-105 — helper: render "Sc 1.3" when the scene has an
    // episode, otherwise plain "Sc 3". Used in the picker AND in the
    // post-save toast so the user always sees an unambiguous label.
    const formatSceneLabel = (s) => {
        if (!s) return "—";
        const num = s.scene_number || "—";
        return s.episode ? `${s.episode}.${num}` : String(num);
    };

    // Iter-119 — Look suggestion stack. Character-specific recents
    // come FIRST (those are the highest-signal recall on set), then
    // project-wide recents the user might want anyway, deduped.
    // When the user has typed something we filter the stack to
    // matches; otherwise we cap at a small visible list.
    const lookSuggestions = useMemo(() => {
        const seen = new Set();
        const merged = [];
        const push = (v) => {
            const key = (v || "").trim();
            if (!key) return;
            const k = key.toLowerCase();
            if (seen.has(k)) return;
            seen.add(k);
            merged.push(key);
        };
        for (const v of characterLooks) push(v);
        for (const v of recentLooks) push(v);
        const q = look.trim().toLowerCase();
        if (!q) return merged.slice(0, 8);
        const exact = merged.find((m) => m.toLowerCase() === q);
        const matches = merged
            .filter((m) => m.toLowerCase().includes(q) && m.toLowerCase() !== q)
            .slice(0, 8);
        // If the user's typed value isn't in history, prepend a
        // "Use \"…\"" hint so they understand pressing Save will
        // create a new look. Implemented via the `__create__`
        // sentinel which the renderer special-cases.
        if (!exact && q.length > 0) {
            return ["__create__", ...matches];
        }
        return matches;
    }, [characterLooks, recentLooks, look]);

    const validateAssign = () => {
        if (characterIds.length === 0) return "Pick at least one character.";
        if (entryMode === "onset" && sceneIds.length === 0) return "Pick at least one scene for an On Set entry.";
        return "";
    };

    // iter-71: multi-select toggle for characters. Idempotent.
    // Iter-118: EDIT mode is now ALSO multi-select (the new /sync
    // endpoint replaces the per-row PATCH so the user can add/remove
    // characters in one save).
    const toggleCharacter = (id) => {
        if (!id) return;
        setCharacterIds((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            return [...prev, id];
        });
    };
    const removeCharacter = (id) => {
        setCharacterIds((prev) => prev.filter((x) => x !== id));
    };
    const clearCharacters = () => setCharacterIds([]);

    // Iter-118 — scene multi-select toggles. Mirrors the cast
    // toggles so the picker UX is identical for both axes.
    const toggleScene = (id) => {
        if (!id) return;
        setSceneIds((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            return [...prev, id];
        });
    };
    const removeScene = (id) => {
        setSceneIds((prev) => prev.filter((x) => x !== id));
    };
    const clearScenes = () => setSceneIds([]);

    const _uploadAndCreate = async (img, basePayload) => {
        let mediaId = null;
        if (img?.file) {
            const media = await uploadProjectMedia(projectId, img.file, {
                display: img.full,
                thumbnail: img.thumb,
                apiBase,
            });
            mediaId = media?.id || null;
        }
        const payload = { ...basePayload };
        if (mediaId) {
            payload.media_id = mediaId;
        } else {
            payload.image_data = img.full;
            payload.thumbnail_data = img.thumb;
        }
        // iter-71: branch by assigned flag.
        //   * Unassigned ("Save unassigned" / "Assign later") → singular
        //     /state-entries — no characters yet, so bulk-assign rejects
        //     it by design.
        //   * Assigned → /state-entries/bulk-assign — creates one
        //     state_entry per character, all sharing the SAME media_id
        //     (image file stored exactly once in GridFS).
        if (basePayload.assigned === false) {
            const { data } = await api.post(
                `${apiBase}/${projectId}/state-entries`,
                payload,
            );
            return data;
        }
        const { data } = await api.post(
            `${apiBase}/${projectId}/state-entries/bulk-assign`,
            payload,
        );
        const entries = Array.isArray(data?.entries) ? data.entries : [];
        const lead = entries[entries.length - 1] || null;
        if (lead) lead._bulk = entries;
        return lead;
    };

    /**
     * iter-58 — when an upload fails because the device has no
     * connection, we DON'T want the user to lose what they entered.
     * We enqueue a "pending upload" descriptor for each image in the
     * batch (with the same shared metadata) and toast the user. The
     * dialog still closes as if successful so the user can keep
     * working — the future flush worker will retry these once the
     * device is back online.
     *
     * Note: at this stage there is no auto-flush. The queue is purely
     * local persistence; iter-59 will add the worker that drains it.
     */
    const _enqueueBatchOffline = (basePayload) => {
        const batchId = `batch_${Date.now().toString(36)}`;
        for (const img of imageBatch) {
            const payload = {
                ...basePayload,
                image_data: img.full,
                thumbnail_data: img.thumb,
            };
            enqueuePendingUpload({
                project_id: projectId,
                kind: "state_entry",
                batch_id: batchId,
                payload,
            });
        }
    };

    const save = async () => {
        const err = validateAssign();
        if (err) {
            setError(err);
            return;
        }
        setSaving(true);
        setError("");
        try {
            if (mode === "edit" && initialEntry) {
                // Iter-118 — EDIT now uses /state-entries/sync. The
                // server replaces every state_entry row that shares
                // this image's media_id with the desired (scene ×
                // character) Cartesian, and updates shared metadata
                // (mode/look/note) on every survivor in one round
                // trip. Falls back to the legacy single-row PATCH for
                // legacy inline images that have no media_id.
                if (initialEntry.media_id) {
                    const { data } = await api.post(
                        `${apiBase}/${projectId}/state-entries/sync`,
                        {
                            media_id: initialEntry.media_id,
                            mode: entryMode,
                            scene_ids: entryMode === "onset" ? sceneIds : sceneIds,
                            character_ids: characterIds,
                            state_label: stateLabel,
                            custom_label:
                                stateLabel === "custom" ? customLabel.trim() : null,
                            look: look.trim() || null,
                            note: note.trim() || null,
                        },
                    );
                    toast.success("Assignment updated");
                    // Pick the entry that best represents the current
                    // viewing context for the receiver (prefer the
                    // exact entry id we opened with, else first row).
                    const fresh = (data?.entries || []).find(
                        (e) => e.id === initialEntry.id,
                    ) || data?.entries?.[0] || null;
                    if (fresh) {
                        fresh._grouping = {
                            scene_ids: data.scene_ids || [],
                            character_ids: data.character_ids || [],
                            entries: data.entries || [],
                        };
                    }
                    onSaved?.(fresh);
                    close(true);
                    return;
                }
                // Legacy inline image (no media_id) — single-row PATCH.
                const lone = characterIds[0];
                const sole = sceneIds[0] || null;
                const { data } = await api.patch(
                    `${apiBase}/${projectId}/state-entries/${initialEntry.id}`,
                    {
                        mode: entryMode,
                        character_id: lone,
                        scene_id: entryMode === "onset" ? sole : sole || null,
                        state_label: stateLabel,
                        custom_label: stateLabel === "custom" ? customLabel.trim() : null,
                        look: look.trim() || null,
                        note: note.trim() || null,
                    }
                );
                toast.success("Entry assigned");
                onSaved?.(data);
                close(true);
                return;
            }
            // Create flow — uploads each image to GridFS in turn (sequential to
            // keep low-bandwidth on-set 4G alive) and posts a state_entry
            // per (image × character) via the bulk-assign endpoint with the
            // SAME shared metadata. Single-character × single-image is the
            // batch-of-1 special case so there's only one code path.
            // iter-74 PART 1 — broadcast an "uploading" pulse so the
            // header pill can switch to "Uploading…" during the
            // round-trips. The matching "saved" event fires after
            // close() below.
            window.dispatchEvent(
                new CustomEvent("onsetflow:upload-state", {
                    detail: {
                        state: "uploading",
                        project_id: projectId,
                        image_count: imageBatch.length,
                        character_count: characterIds.length,
                    },
                }),
            );
            const basePayload = {
                mode: entryMode,
                character_ids: characterIds,
                // Iter-118 — multi-scene Cartesian. Backend creates
                // one state_entry per (scene × character) pair, all
                // sharing the same media_id (image stored once).
                scene_ids: entryMode === "onset" ? sceneIds : sceneIds,
                state_label: stateLabel,
                custom_label: stateLabel === "custom" ? customLabel.trim() : null,
                look: look.trim() || null,
                note: note.trim() || null,
                assigned: true,
            };
            const created = [];
            try {
                for (let i = 0; i < imageBatch.length; i++) {
                    setBatchIndex(i + 1);
                    // eslint-disable-next-line no-await-in-loop
                    const data = await _uploadAndCreate(imageBatch[i], basePayload);
                    created.push(data);
                }
            } catch (uploadErr) {
                if (isOfflineError(uploadErr)) {
                    // Enqueue ONLY the entries that hadn't been created
                    // yet so we don't double-write the ones that
                    // succeeded before the connection dropped.
                    const remaining = imageBatch.slice(created.length);
                    if (remaining.length > 0) {
                        const batchId = `batch_${Date.now().toString(36)}`;
                        for (const img of remaining) {
                            enqueuePendingUpload({
                                project_id: projectId,
                                kind: "state_entry",
                                batch_id: batchId,
                                payload: {
                                    ...basePayload,
                                    image_data: img.full,
                                    thumbnail_data: img.thumb,
                                },
                            });
                        }
                        toast.warning(
                            remaining.length > 1
                                ? `No connection — ${remaining.length} images saved locally, will retry when online`
                                : "No connection — image saved locally, will retry when online",
                        );
                    }
                    if (created.length > 0) {
                        // At least one entry made it through before the drop.
                        onSaved?.(created[created.length - 1]);
                    }
                    close(true);
                    return;
                }
                throw uploadErr;
            }
            // iter-72 PART 3 — richer confirmation toast.
            // Spell out the destination so the user instantly sees
            // "Image saved to Scene 1 — Sergeant Powell" rather than
            // a generic "Entry saved". Falls back gracefully when
            // multi-image / multi-character or unassigned.
            // Iter-118 — primary scene is the first picked one;
            // multi-scene flag tweaks the toast.
            const primarySceneId = sceneIds[0] || null;
            const destScene = primarySceneId
                ? scenes.find((s) => s.id === primarySceneId)
                : null;
            const destCharNames = characterIds
                .map((id) => characters.find((c) => c.id === id)?.name)
                .filter(Boolean);
            const sceneTag = destScene
                ? sceneIds.length > 1
                    ? `${sceneIds.length} scenes`
                    : `Sc ${formatSceneLabel(destScene)}`
                : entryMode === "prep"
                    ? "Fittings"
                    : null;
            let toastMsg;
            if (imageBatch.length > 1 && characterIds.length > 1) {
                toastMsg = `${imageBatch.length} images × ${characterIds.length} characters saved`;
            } else if (imageBatch.length > 1) {
                const charPart = destCharNames.length === 1 ? ` — ${destCharNames[0]}` : "";
                toastMsg = `${imageBatch.length} images saved${sceneTag ? ` to ${sceneTag}` : ""}${charPart}`;
            } else if (characterIds.length > 1) {
                toastMsg = `Image saved${sceneTag ? ` to ${sceneTag}` : ""} — ${characterIds.length} characters`;
            } else {
                const charPart = destCharNames[0] ? ` — ${destCharNames[0]}` : "";
                toastMsg = `Image saved${sceneTag ? ` to ${sceneTag}` : ""}${charPart}`;
            }
            toast.success(toastMsg);
            // iter-72 PART 2 — broadcast the FULL list of created
            // entries so listeners (ImageDetail, StateStream,
            // CharacterDetail, SceneDetail) can refetch the right
            // slice without guessing. The detail also includes
            // destination ids so the receiving page can decide
            // whether the new entry belongs in its current view.
            const allCreated = created.flatMap((c) =>
                Array.isArray(c?._bulk) ? c._bulk : c ? [c] : [],
            );
            window.dispatchEvent(
                new CustomEvent("onsetflow:state-entry-saved", {
                    detail: {
                        project_id: projectId,
                        scene_id: primarySceneId,
                        scene_ids: sceneIds,
                        character_ids: characterIds,
                        mode: entryMode,
                        entries: allCreated,
                        new_entry_ids: allCreated.map((e) => e?.id).filter(Boolean),
                    },
                }),
            );
            // iter-74 PART 1 — fire the matching "saved" pulse so
            // the header pill can flash a brief "Saved" state and
            // then return to its idle (or offline-pending) look.
            window.dispatchEvent(
                new CustomEvent("onsetflow:upload-state", {
                    detail: {
                        state: "saved",
                        project_id: projectId,
                        image_count: imageBatch.length,
                        character_count: characterIds.length,
                    },
                }),
            );
            // For backward compatibility we still call onSaved with the
            // single (last) entry; the calling page typically only
            // refreshes its own data on this signal.
            onSaved?.(created[created.length - 1] || null);
            close(true);
        } catch (e) {
            // iter-74 PART 1 — abort the "uploading" pulse so the
            // header pill doesn't stay stuck on the spinner when
            // the request fails.
            window.dispatchEvent(
                new CustomEvent("onsetflow:upload-state", {
                    detail: { state: "idle", project_id: projectId },
                }),
            );
            // iter-58: silent enqueue happens above for partial-batch
            // network failures. Anything reaching here is a real API
            // error that the user needs to see (validation, 4xx/5xx).
            if (isOfflineError(e)) {
                // Edge case — the very first request failed before
                // _uploadAndCreate even ran. Enqueue the whole batch
                // and dismiss.
                _enqueueBatchOffline({
                    mode: entryMode,
                    character_ids: characterIds,
                    scene_ids: entryMode === "onset" ? sceneIds : sceneIds,
                    state_label: stateLabel,
                    custom_label: stateLabel === "custom" ? customLabel.trim() : null,
                    look: look.trim() || null,
                    note: note.trim() || null,
                    assigned: true,
                });
                toast.warning(
                    imageBatch.length > 1
                        ? `No connection — ${imageBatch.length} images saved locally, will retry when online`
                        : "No connection — image saved locally, will retry when online",
                );
                close(true);
                return;
            }
            setError(formatApiError(e));
        } finally {
            setSaving(false);
            setBatchIndex(0);
        }
    };

    const assignLater = async () => {
        setLater(true);
        setError("");
        try {
            const basePayload = {
                note: note.trim() || null,
                assigned: false,
            };
            const created = [];
            try {
                for (let i = 0; i < imageBatch.length; i++) {
                    setBatchIndex(i + 1);
                    // eslint-disable-next-line no-await-in-loop
                    const data = await _uploadAndCreate(imageBatch[i], basePayload);
                    created.push(data);
                }
            } catch (uploadErr) {
                if (isOfflineError(uploadErr)) {
                    const remaining = imageBatch.slice(created.length);
                    if (remaining.length > 0) {
                        const batchId = `batch_${Date.now().toString(36)}`;
                        for (const img of remaining) {
                            enqueuePendingUpload({
                                project_id: projectId,
                                kind: "state_entry",
                                batch_id: batchId,
                                payload: {
                                    ...basePayload,
                                    image_data: img.full,
                                    thumbnail_data: img.thumb,
                                },
                            });
                        }
                        toast.warning(
                            "No connection — images saved locally, will retry when online",
                        );
                    }
                    if (created.length > 0) {
                        onAssignLater?.(created[created.length - 1]);
                    }
                    close(true);
                    return;
                }
                throw uploadErr;
            }
            toast.warning(
                imageBatch.length > 1
                    ? `${imageBatch.length} images saved unassigned`
                    : "Image saved but not assigned to a scene",
            );
            onAssignLater?.(created[created.length - 1] || null);
            close(true);
        } catch (e) {
            if (isOfflineError(e)) {
                _enqueueBatchOffline({
                    note: note.trim() || null,
                    assigned: false,
                });
                toast.warning(
                    "No connection — images saved locally, will retry when online",
                );
                close(true);
                return;
            }
            setError(formatApiError(e));
        } finally {
            setLater(false);
            setBatchIndex(0);
        }
    };

    const busy = saving || later;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && close()}>
            <DialogContent
                data-testid="assignment-dialog"
                className="max-w-lg w-full max-h-[92vh] sm:max-h-[88vh] flex flex-col bg-card border-white/10 p-0 gap-0 sm:rounded-xl"
            >
                {/* Header — pinned so the user always sees what they're doing. */}
                <DialogHeader className="px-4 pt-4 pb-3 border-b border-white/10 space-y-2 pr-12">
                    <div className="flex items-start gap-3">
                        {isBatch ? (
                            <div
                                data-testid="assignment-batch-preview"
                                className="shrink-0 grid grid-cols-3 gap-1 max-w-[164px]"
                            >
                                {imageBatch.slice(0, 6).map((img, i) => (
                                    <img
                                        key={i}
                                        src={img.thumb || img.full}
                                        alt=""
                                        className="h-12 w-12 rounded-md object-cover border border-white/10 bg-black"
                                    />
                                ))}
                                {imageBatch.length > 6 ? (
                                    <div className="h-12 w-12 rounded-md border border-white/10 bg-black/60 flex items-center justify-center text-[10px] font-bold text-white">
                                        +{imageBatch.length - 6}
                                    </div>
                                ) : null}
                            </div>
                        ) : (image?.thumb || initialEntry) ? (
                            <img
                                data-testid="assignment-preview"
                                src={image?.thumb || entryThumbSrc(projectId, initialEntry, apiBase)}
                                alt="preview"
                                className="h-12 w-12 shrink-0 rounded-md object-cover border border-white/10 bg-black"
                            />
                        ) : null}
                        <div className="min-w-0 flex-1">
                            <DialogTitle className="font-display text-lg font-bold text-white leading-tight">
                                {mode === "edit"
                                    ? "Assign this entry"
                                    : isBatch
                                    ? `Assign ${imageBatch.length} captures`
                                    : "Assign capture"}
                            </DialogTitle>
                            <DialogDescription className="text-[11px] text-neutral-400 leading-snug">
                                {mode === "edit"
                                    ? "Pick mode, scene and character."
                                    : isBatch
                                    ? "These settings apply to ALL images in the batch."
                                    : "Pick where this image belongs — or save to Unassigned."}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                {/* Scrollable middle. Sticky footer below sits OUTSIDE this so
                    the Save button stays visible on small screens. */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
                    {error ? (
                        <p data-testid="assignment-error" className="text-sm text-red-400">
                            {error}
                        </p>
                    ) : null}

                    {/* Iter-119 — Mode pill toggle. Tucked into a single
                        compact row so it stops dominating the screen.
                        On Set is the default and most-used path; the
                        toggle is still one-tap accessible. */}
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 shrink-0">
                            Mode
                        </span>
                        <div
                            data-testid="assignment-mode"
                            role="tablist"
                            className="inline-flex items-center bg-secondary p-0.5 rounded-full gap-0.5"
                        >
                            <button
                                type="button"
                                role="tab"
                                aria-selected={entryMode === "onset"}
                                data-testid="assignment-mode-onset"
                                data-active={entryMode === "onset" ? "true" : "false"}
                                onClick={() => setEntryMode("onset")}
                                className={`instant-tap h-8 px-3.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${
                                    entryMode === "onset"
                                        ? "bg-primary text-primary-foreground shadow-sm"
                                        : "text-neutral-300 hover:text-white"
                                }`}
                            >
                                <Clapperboard size={13} />
                                On Set
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={entryMode === "prep"}
                                data-testid="assignment-mode-prep"
                                data-active={entryMode === "prep" ? "true" : "false"}
                                onClick={() => setEntryMode("prep")}
                                className={`instant-tap h-8 px-3.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${
                                    entryMode === "prep"
                                        ? "bg-primary text-primary-foreground shadow-sm"
                                        : "text-neutral-300 hover:text-white"
                                }`}
                            >
                                <ClipboardList size={13} />
                                Fittings
                            </button>
                        </div>
                    </div>

                    {/* Iter-119 — Scenes trigger row. Tapping opens a
                        full-screen searchable picker so the main
                        dialog stays scannable in under 1 second. */}
                    <button
                        type="button"
                        data-testid="assignment-scene-trigger"
                        onClick={() => setScenePickerOpen(true)}
                        disabled={loading}
                        className="instant-tap w-full flex items-center gap-3 px-3 py-3 rounded-lg bg-secondary border border-white/10 hover:bg-white/10 text-left disabled:opacity-60"
                    >
                        <Film size={18} className="text-neutral-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                {entryMode === "onset" ? "Scene · required" : "Scene · optional"}
                            </div>
                            <div className="text-sm text-white truncate">
                                {loading
                                    ? "Loading scenes…"
                                    : sceneIds.length === 0
                                        ? entryMode === "onset"
                                            ? "Tap to pick scene(s)"
                                            : "Tap to add scene(s)"
                                        : (() => {
                                            const labels = sceneIds
                                                .map((id) => {
                                                    const s = scenes.find((x) => x.id === id);
                                                    return s ? `Sc ${formatSceneLabel(s)}${s.heading ? ` · ${s.heading}` : ""}` : null;
                                                })
                                                .filter(Boolean);
                                            if (labels.length === 0) return `${sceneIds.length} selected`;
                                            if (labels.length === 1) return labels[0];
                                            return `${labels.length} scenes · ${labels[0].split(" · ")[0]}${labels[1] ? `, ${labels[1].split(" · ")[0]}` : ""}${labels.length > 2 ? ` +${labels.length - 2}` : ""}`;
                                        })()}
                            </div>
                        </div>
                        <ChevronRight size={16} className="text-neutral-500 shrink-0" />
                    </button>

                    {/* Iter-119 — Characters trigger row. Tapping
                        opens a full-screen multi-select picker with
                        team filter, search, and inline-create form. */}
                    <button
                        type="button"
                        data-testid="assignment-char-trigger"
                        onClick={() => setCharPickerOpen(true)}
                        disabled={loading}
                        className="instant-tap w-full flex items-center gap-3 px-3 py-3 rounded-lg bg-secondary border border-white/10 hover:bg-white/10 text-left disabled:opacity-60"
                    >
                        <UsersIcon size={18} className="text-neutral-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                {characterIds.length === 0 ? "Characters · required" : `Characters · ${characterIds.length}`}
                            </div>
                            <div className="text-sm text-white truncate">
                                {loading
                                    ? "Loading cast…"
                                    : characterIds.length === 0
                                        ? "Tap to pick characters"
                                        : (() => {
                                            const names = characterIds
                                                .map((id) => characters.find((x) => x.id === id)?.name)
                                                .filter(Boolean);
                                            if (names.length === 0) return `${characterIds.length} selected`;
                                            if (names.length === 1) return names[0];
                                            if (names.length === 2) return names.join(", ");
                                            return `${names[0]}, ${names[1]} +${names.length - 2}`;
                                        })()}
                            </div>
                        </div>
                        <ChevronRight size={16} className="text-neutral-500 shrink-0" />
                    </button>

                    {/* Iter-119 — Look combobox. A single text input
                        with a focus-driven suggestion popover. Order
                        is: character-specific recents → project-wide
                        recents (deduped). Typing filters the list and
                        shows a "Use \"…\"" hint for new looks so the
                        user knows pressing Save will create one. */}
                    <div ref={lookWrapRef} className="relative">
                        <Label
                            htmlFor="assignment-look"
                            className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Look · optional
                        </Label>
                        <div className="relative mt-1.5">
                            <Sparkles
                                size={14}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                            />
                            <Input
                                ref={lookInputRef}
                                id="assignment-look"
                                data-testid="assignment-look"
                                value={look}
                                onChange={(e) => setLook(e.target.value)}
                                onFocus={() => setLookFocused(true)}
                                placeholder={
                                    characterIds.length > 0
                                        ? "e.g. Clean, Bloody nose, Take 3"
                                        : "Look (optional)"
                                }
                                maxLength={60}
                                autoComplete="off"
                                className="pl-9 pr-9 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary text-base"
                            />
                            {look ? (
                                <button
                                    type="button"
                                    data-testid="assignment-look-clear"
                                    onClick={() => {
                                        setLook("");
                                        lookInputRef.current?.focus();
                                    }}
                                    aria-label="Clear look"
                                    title="Clear look"
                                    className="instant-tap absolute top-1/2 right-1.5 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-white hover:bg-white/10"
                                >
                                    <span className="text-base leading-none">×</span>
                                </button>
                            ) : null}
                        </div>
                        {lookFocused && lookSuggestions.length > 0 ? (
                            <div
                                data-testid="assignment-look-suggestions"
                                className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-white/10 bg-card shadow-xl max-h-56 overflow-y-auto py-1"
                            >
                                {lookSuggestions.map((v) => {
                                    if (v === "__create__") {
                                        const typed = look.trim();
                                        return (
                                            <button
                                                key="__create__"
                                                type="button"
                                                data-testid="assignment-look-create"
                                                onClick={() => {
                                                    setLook(typed);
                                                    setLookFocused(false);
                                                }}
                                                className="instant-tap w-full text-left px-3 py-2 text-sm hover:bg-white/5 inline-flex items-center gap-2"
                                            >
                                                <Sparkles size={13} className="text-primary shrink-0" />
                                                <span className="text-neutral-300">Use</span>
                                                <span className="font-semibold text-white truncate">
                                                    “{typed}”
                                                </span>
                                            </button>
                                        );
                                    }
                                    const isPicked = (look || "").trim().toLowerCase() === v.toLowerCase();
                                    const isCharSpecific = characterLooks.includes(v);
                                    return (
                                        <button
                                            key={v}
                                            type="button"
                                            data-testid={`assignment-look-suggest-${v}`}
                                            data-active={isPicked ? "true" : "false"}
                                            onClick={() => {
                                                setLook(v);
                                                setLookFocused(false);
                                            }}
                                            className={`instant-tap w-full text-left px-3 py-2 text-sm hover:bg-white/5 inline-flex items-center gap-2 ${
                                                isPicked ? "text-white font-semibold" : "text-neutral-200"
                                            }`}
                                        >
                                            <span className="flex-1 truncate">{v}</span>
                                            <span
                                                className={`text-[10px] uppercase tracking-[0.15em] shrink-0 ${
                                                    isCharSpecific ? "text-primary/80" : "text-neutral-500"
                                                }`}
                                            >
                                                {isCharSpecific ? "for this cast" : "recent"}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>

                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Note
                        </Label>
                        <Textarea
                            data-testid="assignment-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Optional — first line shows in the stream"
                            rows={2}
                            maxLength={2000}
                            className="mt-1.5 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary resize-none"
                        />
                    </div>
                </div>

                {/* Sticky action footer — always visible regardless of scroll
                    position so the user never has to hunt for Save. */}
                <div className="px-4 py-3 border-t border-white/10 bg-card flex flex-col sm:flex-row-reverse gap-2">
                    <Button
                        type="button"
                        data-testid="assignment-save"
                        disabled={busy}
                        onClick={save}
                        className="min-tap w-full sm:flex-1 font-semibold tracking-wide"
                    >
                        {saving ? (
                            <>
                                <Loader2 size={14} className="mr-2 animate-spin" />
                                {isBatch
                                    ? `Saving ${batchIndex || 1}/${imageBatch.length}…`
                                    : "Saving…"}
                            </>
                        ) : mode === "edit" ? (
                            "Save assignment"
                        ) : isBatch && (characterIds.length > 1 || sceneIds.length > 1) ? (
                            `Save ${imageBatch.length} × ${sceneIds.length || 1}s × ${characterIds.length}c`
                        ) : isBatch ? (
                            `Save & assign all (${imageBatch.length})`
                        ) : sceneIds.length > 1 && characterIds.length > 1 ? (
                            `Save to ${sceneIds.length} scenes × ${characterIds.length} chars`
                        ) : sceneIds.length > 1 ? (
                            `Save to ${sceneIds.length} scenes`
                        ) : characterIds.length > 1 ? (
                            `Save to ${characterIds.length} characters`
                        ) : (
                            "Save & Assign"
                        )}
                    </Button>
                    {mode === "create" ? (
                        <Button
                            type="button"
                            data-testid="assignment-later"
                            variant="secondary"
                            disabled={busy}
                            onClick={assignLater}
                            className="min-tap w-full sm:w-auto"
                        >
                            {later ? (
                                <>
                                    <Loader2 size={14} className="mr-2 animate-spin" />
                                    {isBatch
                                        ? `Saving ${batchIndex || 1}/${imageBatch.length}…`
                                        : "Saving…"}
                                </>
                            ) : (
                                <>
                                    <Clock size={14} className="mr-2" />
                                    {isBatch ? `Save unassigned (${imageBatch.length})` : "Assign later"}
                                </>
                            )}
                        </Button>
                    ) : null}
                </div>

                {/* Iter-119 — Full-screen pickers. Rendered inside
                    DialogContent so they sit above the form layer.
                    State is owned by the parent so closing the picker
                    preserves selections automatically. */}
                <AssignmentScenePicker
                    open={scenePickerOpen}
                    onClose={() => setScenePickerOpen(false)}
                    scenes={scenes}
                    filteredScenes={filteredScenes}
                    episodes={episodes}
                    episodeFilter={episodeFilter}
                    setEpisodeFilter={(ep) => {
                        setEpisodeFilter(ep);
                        // Iter-118 — picking a different episode
                        // invalidates any previously-chosen scenes
                        // that don't belong to it. Drop only the
                        // mis-targeted ones, keep matches.
                        if (ep && sceneIds.length > 0) {
                            setSceneIds((prev) =>
                                prev.filter((sid) => {
                                    const cur = scenes.find((s) => s.id === sid);
                                    return cur && String(cur.episode || "") === String(ep);
                                }),
                            );
                        }
                    }}
                    selectedIds={sceneIds}
                    onToggle={toggleScene}
                    onClear={clearScenes}
                    query={sceneQuery}
                    setQuery={setSceneQuery}
                    formatSceneLabel={formatSceneLabel}
                    loading={loading}
                    entryMode={entryMode}
                />
                <AssignmentCharacterPicker
                    open={charPickerOpen}
                    onClose={() => setCharPickerOpen(false)}
                    characters={characters}
                    filteredCharacters={filteredCharacters}
                    selectedIds={characterIds}
                    onToggle={toggleCharacter}
                    onClear={clearCharacters}
                    teamFilter={teamFilter}
                    setTeamFilter={setTeamFilter}
                    query={charQuery}
                    setQuery={setCharQuery}
                    loading={loading}
                    canCreateCharacters={canCreateCharacters}
                    creatingChar={creatingChar}
                    setCreatingChar={setCreatingChar}
                    hasPendingRequest={hasPendingRequest}
                    requestingPerm={requestingPerm}
                    onRequestPerm={requestCreatePermission}
                    newCharNumber={newCharNumber}
                    setNewCharNumber={setNewCharNumber}
                    newCharName={newCharName}
                    setNewCharName={setNewCharName}
                    newCharActor={newCharActor}
                    setNewCharActor={setNewCharActor}
                    newCharTeam={newCharTeam}
                    setNewCharTeam={setNewCharTeam}
                    creatingCharSubmitting={creatingCharSubmitting}
                    onCreateCharacter={createCharacterInline}
                />
            </DialogContent>
        </Dialog>
    );
}
